/**
 * Searchspring Facets Component
 *
 * Manages facet state, filtering, and UI updates for Searchspring-powered collections.
 * Handles visual facets, color swatches, size filters, and standard text facets.
 */

import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/** Caret (triangle down) SVG used in facet summaries */
const CARET_SVG =
  '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** Checkbox check path used in list item checkboxes */
const CHECK_SVG =
  '<path d="M2 6L5 9L10 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';

/** Alpha size order for sorting letter sizes */
const ALPHA_SIZE_ORDER = [
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '2XL',
  'XXXL',
  '3XL',
  '4XL',
  'ONE SIZE',
  'OS',
  'OSFM',
];

/** Regex to detect letter-size values */
const ALPHA_SIZE_PATTERN = /^(XXS|XS|S|M|L|XL|XXL|XXXL|2XL|3XL|4XL|ONE SIZE|OS|OSFM)$/i;

/**
 * @typedef {object} FacetValue
 * @property {string} label - Display label
 * @property {string} value - Filter value
 * @property {number} count - Result count
 * @property {boolean} active - Whether currently selected
 * @property {string} [image] - Image URL for visual facets
 * @property {string} [color] - Color value for swatches
 */

/**
 * @typedef {object} Facet
 * @property {string} field - Searchspring field name
 * @property {string} label - Display label
 * @property {string} type - Facet type (list, grid, slider, palette)
 * @property {FacetValue[]} values - Available values
 * @property {boolean} multiple - Allow multiple selections
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement} [facetsContainer] - Container for all facets
 * @property {HTMLElement[]} [facetItems] - Individual facet elements
 * @property {HTMLElement} [clearAllButton] - Clear all filters button
 */

/**
 * @typedef {{ features?: { visualFacets?: boolean; colorSwatches?: boolean; splitSizeFilters?: boolean }; colorSwatchPrefix?: string }} SearchspringFacetsConfig
 */

class SearchspringFacetsComponent extends Component {
  #controller = new AbortController();
  /** @type {Facet[] | null} */
  #lastFacets = null;
  /** @type {{ key: string; map: Map<string, Array<{ value: string; label: string }>> } | null} */
  #facetsReplacementMapCache = null;

  /**
   * Get Searchspring configuration
   * @returns {SearchspringFacetsConfig}
   */
  get config() {
    return /** @type {SearchspringFacetsConfig} */ (window.searchspringConfig || {});
  }

  /**
   * Whether this component is in drawer mode (accordion with +/- icons, hide empty facets)
   */
  get drawerMode() {
    return this.hasAttribute('data-drawer') || this.dataset.drawer === 'true';
  }

  /**
   * Drawer on desktop: show facets expanded (no accordions). Mobile drawer keeps accordions.
   */
  get drawerDesktopMode() {
    return (
      this.drawerMode &&
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 750px)').matches
    );
  }

  /**
   * Check if visual facets are enabled
   */
  get visualFacetsEnabled() {
    return this.config.features?.visualFacets ?? true;
  }

  /**
   * Check if color swatches are enabled
   */
  get colorSwatchesEnabled() {
    return this.config.features?.colorSwatches ?? true;
  }

  /**
   * Check if split size filters are enabled
   */
  get splitSizeFiltersEnabled() {
    return this.config.features?.splitSizeFilters ?? true;
  }

  /**
   * Get color swatch URL prefix
   */
  get colorSwatchPrefix() {
    return this.config.colorSwatchPrefix || '';
  }

  /**
   * Locale string for the jeans-size split section label (e.g. "Jeans size").
   * Sourced from data-size-split-label attribute set in Liquid.
   * @returns {string}
   */
  get #sizeSplitLabel() {
    return this.dataset.sizeSplitLabel || '';
  }

  /**
   * Set of facet field names to hide from the drawer.
   * Parsed from data-facets-hide (JSON array of field name strings).
   * @returns {Set<string>}
   */
  get #facetsHideSet() {
    const raw = this.dataset.facetsHide ?? '[]';
    try {
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return new Set();
      return new Set(arr.filter((f) => typeof f === 'string' && f.length > 0));
    } catch {
      return new Set();
    }
  }

  /**
   * Replacement options for drawer facets: field -> [{ value, label }].
   * Parsed from data-facets-replacement (JSON array of { field, value, label }). Cached by raw string.
   * @returns {Map<string, Array<{ value: string, label: string }>>}
   */
  get #facetsReplacementMap() {
    const raw = this.dataset.facetsReplacement ?? '[]';
    if (this.#facetsReplacementMapCache?.key === raw) {
      return this.#facetsReplacementMapCache.map;
    }
    try {
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) {
        this.#facetsReplacementMapCache = { key: raw, map: new Map() };
        return this.#facetsReplacementMapCache.map;
      }
      const map = new Map();
      for (const item of arr) {
        if (item?.field && item?.value != null) {
          const label = item.label ?? item.value;
          if (!map.has(item.field)) map.set(item.field, []);
          map.get(item.field).push({ value: String(item.value), label: String(label) });
        }
      }
      this.#facetsReplacementMapCache = { key: raw, map };
      return map;
    } catch {
      this.#facetsReplacementMapCache = { key: raw, map: new Map() };
      return this.#facetsReplacementMapCache.map;
    }
  }

  /**
   * Transform facets data for ss_size_clothing: partition values into clothing sizes (label
   * contains `/`) and jeans sizes (label has no `/`). If both groups are present, split the
   * facet into a primary clothing group and a labelled jeans-size section. If no value has `/`,
   * all values are jeans sizes — relabel the entire facet.
   *
   * Conditions (only applies to field === 'ss_size_clothing'):
   *   1. Mixed — values WITH `/` (clothing) AND values WITHOUT `/` (jeans) both present:
   *      → { ...facet, values: clothingValues, splitLabel, splitValues: jeansValues }
   *   2. No `/` anywhere — all values are jeans sizes:
   *      → { ...facet, label: sizeSplitLabel }
   *   3. All have `/` — all clothing sizes → return facet unchanged.
   *
   * @param {Facet[]} facets
   * @returns {Facet[]}
   */
  #buildFinalFacets(facets) {
    const sizeSplitLabel = this.#sizeSplitLabel;
    if (!sizeSplitLabel) return facets;

    return facets.map((facet) => {
      if (facet.field !== 'ss_size_clothing') return facet;

      const clothingValues = facet.values.filter((v) => v?.label?.includes('/'));
      const jeansValues = facet.values.filter((v) => !v?.label?.includes('/'));

      // No `/` anywhere — all plain values are jeans sizes, relabel the facet
      if (clothingValues.length === 0) {
        return { ...facet, label: sizeSplitLabel };
      }

      // Mixed — split into clothing (primary) and jeans (split section)
      if (jeansValues.length > 0) {
        return {
          ...facet,
          values: clothingValues,
          splitLabel: sizeSplitLabel,
          splitValues: jeansValues,
        };
      }

      return facet;
    });
  }

  /**
   * Resolve a ref (single element or first of array)
   * @param {keyof Refs} key
   * @returns {HTMLElement | undefined}
   */
  #getRef(key) {
    const ref = this.refs[key];
    const el = Array.isArray(ref) ? ref[0] : ref;
    return el instanceof HTMLElement ? el : undefined;
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    // Listen for facets update events from results list
    document.addEventListener('ss:facets-update', this.#handleFacetsUpdate, { signal });

    // Listen for filter changes
    this.addEventListener('change', this.#handleFilterChange, { signal });

    // Listen for clear all click
    this.addEventListener('click', this.#handleClick, { signal });

    // Drawer: re-render when crossing desktop breakpoint so accordion vs regular layout updates
    if (this.drawerMode) {
      const mq = window.matchMedia('(min-width: 750px)');
      mq.addEventListener(
        'change',
        () => {
          if (this.#lastFacets) this.#renderFacets(this.#lastFacets);
        },
        { signal },
      );
      // Re-render when filters drawer opens so desktop gets static facets (in case facets were rendered at narrow width)
      document.addEventListener(
        'dialog:open',
        (e) => {
          const opener = e.target instanceof Element ? e.target : null;
          const isFiltersDrawer =
            opener?.id === 'filters-drawer' || opener?.closest?.('#filters-drawer');
          if (
            isFiltersDrawer &&
            this.closest('#filters-drawer') &&
            this.#lastFacets &&
            window.matchMedia('(min-width: 750px)').matches
          ) {
            this.#renderFacets(this.#lastFacets);
          }
        },
        { signal },
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handle facets update event
   * @param {CustomEvent} event
   */
  #handleFacetsUpdate = (event) => {
    const { facets } = event.detail;
    if (!facets) return;

    this.#renderFacets(facets);
  };

  /**
   * Render all facets
   * @param {Facet[]} facets
   */
  #renderFacets(facets) {
    const containerEl = this.#getRef('facetsContainer');
    if (!containerEl) return;

    // In drawer mode (mobile only): preserve which accordions were open before replacing DOM
    let openFields = new Set();
    if (this.drawerMode && !this.drawerDesktopMode) {
      containerEl.querySelectorAll('.ss-facet-accordion details[open]').forEach((detailsEl) => {
        const field = detailsEl.closest('.ss-facet-accordion')?.getAttribute('data-facet-field');
        if (field) openFields.add(field);
      });
    }

    // In drawer mode: show facets that have at least one value with count > 0,
    // or that have replacement options (custom list from featured filters)
    const replacementMap = this.#facetsReplacementMap;
    let facetsToRender = facets;
    if (this.drawerMode && replacementMap.size > 0) {
      const facetByField = new Map(facets.map((f) => [f.field, f]));
      for (const field of replacementMap.keys()) {
        if (!facetByField.has(field)) {
          const label = field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
          facetsToRender = [
            ...facetsToRender,
            { field, label, values: [], type: 'list', multiple: true },
          ];
        }
      }
      facetsToRender = facetsToRender.filter(
        (facet) => facet.values.some((v) => v.count > 0) || replacementMap.has(facet.field),
      );
    } else if (this.drawerMode) {
      facetsToRender = facets.filter((facet) => facet.values.some((v) => v.count > 0));
    }

    // Hide facets whose SS field is replaced by a featured filter in the bar
    const hideSet = this.#facetsHideSet;
    if (hideSet.size > 0) {
      facetsToRender = facetsToRender.filter((facet) => !hideSet.has(facet.field));
    }

    this.#lastFacets = facets;

    const finalFacets = this.#buildFinalFacets(facetsToRender);
    const html = finalFacets.map((facet) => this.#renderFacet(facet, replacementMap)).join('');
    containerEl.innerHTML = html;

    // Restore open state for drawer accordions (mobile only)
    if (this.drawerMode && !this.drawerDesktopMode && openFields.size > 0) {
      containerEl.querySelectorAll('.ss-facet-accordion').forEach((accordion) => {
        const field = accordion.getAttribute('data-facet-field');
        if (field && openFields.has(field)) {
          accordion.querySelector('details')?.setAttribute('open', '');
        }
      });
    }

    // Update clear all button visibility
    this.#updateClearAllVisibility();
  }

  /**
   * Render individual facet based on type
   * @param {Facet} facet
   * @param {Map<string, Array<{ value: string, label: string }>>} [replacementMap]
   * @returns {string}
   */
  #renderFacet(facet, replacementMap = new Map()) {
    const fieldLower = facet.field.toLowerCase();
    let innerHtml = '';
    let activeCount = facet.values.filter((v) => v.active).length;
    // Include jeans-size split values in the active count shown in drawer accordion headers
    if (facet.splitValues) {
      activeCount += facet.splitValues.filter((v) => v.active).length;
    }

    const useReplacement =
      this.drawerMode &&
      replacementMap.has(facet.field) &&
      (replacementMap.get(facet.field)?.length ?? 0) > 0;
    if (useReplacement) {
      const options = replacementMap.get(facet.field) ?? [];
      const urlFilterKey = `filter.${facet.field}`;
      const urlSelected =
        typeof window !== 'undefined'
          ? new URL(window.location.href).searchParams.getAll(urlFilterKey) || []
          : [];
      const values = options.map((opt) => {
        const apiVal = facet.values.find((v) => v.value === opt.value);
        const active = apiVal?.active ?? urlSelected.includes(opt.value);
        return {
          value: opt.value,
          label: opt.label,
          count: apiVal?.count ?? 0,
          active,
        };
      });
      activeCount = values.filter((v) => v.active).length;
      innerHtml = this.#renderTextFacetWithValues(facet, values, activeCount);
    } else if (this.visualFacetsEnabled && this.#isVisualFacet(facet)) {
      innerHtml = this.#renderVisualFacet(facet, activeCount);
    } else if (this.colorSwatchesEnabled && this.#isColorFacet(fieldLower)) {
      innerHtml = this.#renderColorSwatchFacet(facet, activeCount);
    } else if (this.splitSizeFiltersEnabled && this.#isSizeFacet(fieldLower)) {
      innerHtml = this.#renderSizeFacet(facet, activeCount);
    } else {
      innerHtml = this.#renderTextFacet(facet, activeCount);
    }

    if (this.drawerMode) {
      if (this.drawerDesktopMode) {
        return `
          <div class="ss-facet ss-facet--drawer-desktop" data-facet-field="${facet.field}">
            <div class="ss-facet__label-heading">${facet.label}${activeCount > 0 ? ` (${activeCount})` : ''}</div>
            <div class="ss-facet__content">${this.#extractFacetContent(innerHtml)}</div>
          </div>
        `;
      }
      return `
        <accordion-custom class="ss-facet-accordion accordion--plus" data-facet-field="${facet.field}">
          <details class="details ss-facet__panel">
            <summary class="details__header facets__summary">
              <span class="ss-facet__label">${facet.label} ${activeCount > 0 ? `<span>(${activeCount})</span>` : ''}</span>
              <span class="ss-facet__accordion-icon">
                <span class="svg-wrapper icon-plus" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 0V14" stroke="black"/>
                    <path d="M14 7L7.2227e-06 7" stroke="black"/>
                  </svg>
                </span>
                <span class="svg-wrapper icon-minus" aria-hidden="true">
                  <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.28 7.14001L2.77828e-05 7.14001" stroke="black"/>
                  </svg>
                </span>
              </span>
            </summary>
            <div class="details-content">${this.#extractFacetContent(innerHtml)}</div>
          </details>
        </accordion-custom>
      `;
    }

    return innerHtml;
  }

  /**
   * Render text/list facet with an explicit values array (for replaced facets).
   * In drawer mode uses the same pill layout as Size so it matches other drawer filters.
   * @param {Facet} facet
   * @param {Array<{ value: string, label: string, count: number, active: boolean }>} values
   * @param {number} activeCount
   * @returns {string}
   */
  #renderTextFacetWithValues(facet, values, activeCount) {
    if (this.drawerMode) {
      const visualItems = values
        .map(
          (value) => `
                <label class="ss-facet__visual-item ${value.active ? 'ss-facet__visual-item--active' : ''} ${value.count === 0 ? 'ss-facet__visual-item--disabled' : ''}">
                  <input
                    type="checkbox"
                    name="filter.${facet.field}"
                    value="${this.#escapeHtml(value.value)}"
                    ${value.active ? 'checked' : ''}
                    ${value.count === 0 ? 'disabled' : ''}
                    class="ss-facet__input visually-hidden"
                  />
                  <span class="ss-facet__visual-label">${this.#escapeHtml(value.label)}</span>
                  <span class="ss-facet__visual-count">(${value.count})</span>
                  ${
                    value.active
                      ? `
                  <button
                    type="button"
                    class="ss-facet__visual-item-remove"
                    aria-label="Remove ${this.#escapeHtml(facet.label)}: ${this.#escapeHtml(value.label)}"
                    data-ss-remove-visual
                    data-field="${this.#escapeHtml(facet.field)}"
                    data-value="${this.#escapeHtml(value.value)}"
                  >
                    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                    </svg>
                  </button>
                  `
                      : ''
                  }
                </label>
              `,
        )
        .join('');
      return `
      <div class="ss-facet ss-facet--visual ss-facet--replaced" data-facet-field="${facet.field}">
        <details class="ss-facet__panel" open>
          <summary class="ss-facet__summary">
            <span class="ss-facet__label">${facet.label}</span>
            ${activeCount > 0 ? `<span>(${activeCount})</span>` : ''}
            <span class="ss-facet__caret">${CARET_SVG}</span>
          </summary>
          <div class="ss-facet__content">
            <div class="ss-facet__visual-grid">
              ${visualItems}
            </div>
          </div>
        </details>
      </div>
    `;
    }

    return `
      <div class="ss-facet ss-facet--text" data-facet-field="${facet.field}">
        <details class="ss-facet__panel" open>
          <summary class="ss-facet__summary">
            <span class="ss-facet__label">${facet.label}</span>
            ${activeCount > 0 ? `<span>(${activeCount})</span>` : ''}
            <span class="ss-facet__caret">${CARET_SVG}</span>
          </summary>
          <div class="ss-facet__content">
            <ul class="ss-facet__list">
              ${values
                .map(
                  (value) => `
                <li class="ss-facet__list-item ${value.active ? 'ss-facet__list-item--active' : ''} ${value.count === 0 ? 'ss-facet__list-item--disabled' : ''}">
                  <label class="ss-facet__checkbox">
                    <input
                      type="checkbox"
                      name="filter.${facet.field}"
                      value="${this.#escapeHtml(value.value)}"
                      ${value.active ? 'checked' : ''}
                      ${value.count === 0 ? 'disabled' : ''}
                      class="ss-facet__input"
                    />
                    <span class="ss-facet__checkbox-box">
                      <svg class="ss-facet__checkbox-check" viewBox="0 0 12 12" fill="none" aria-hidden="true">${CHECK_SVG}</svg>
                    </span>
                    <span class="ss-facet__checkbox-label">${this.#escapeHtml(value.label)}</span>
                    <span class="ss-facet__checkbox-count">(${value.count})</span>
                  </label>
                </li>
              `,
                )
                .join('')}
            </ul>
          </div>
        </details>
      </div>
    `;
  }

  /**
   * Extract the inner content from a facet HTML string (strip outer wrapper for drawer accordion)
   * @param {string} html - Full facet HTML
   * @returns {string}
   */
  #extractFacetContent(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    const facet = div.querySelector('.ss-facet');
    if (!facet) return html;
    const content = facet.querySelector('.ss-facet__content');
    return content ? content.innerHTML : facet.innerHTML;
  }

  /**
   * Escape string for safe use in HTML attributes and text
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Check if facet should be rendered as visual (has images)
   * @param {Facet} facet
   * @returns {boolean}
   */
  #isVisualFacet(facet) {
    return facet.type === 'grid' || facet.values.some((v) => v.image);
  }

  /**
   * Check if facet is a color facet
   * @param {string} field
   * @returns {boolean}
   */
  #isColorFacet(field) {
    return field.includes('color') || field.includes('colour');
  }

  /**
   * Check if facet is a size facet
   * @param {string} field
   * @returns {boolean}
   */
  #isSizeFacet(field) {
    return field.includes('size');
  }

  /**
   * Render a list of values as visual grid items
   * @param {string} field
   * @param {FacetValue[]} values
   * @returns {string}
   */
  #renderVisualItems(field, values) {
    return values
      .map(
        (value) => `
        <label class="ss-facet__visual-item ${value.active ? 'ss-facet__visual-item--active' : ''} ${value.count === 0 ? 'ss-facet__visual-item--disabled' : ''}">
          <input
            type="checkbox"
            name="filter.${field}"
            value="${value.value}"
            ${value.active ? 'checked' : ''}
            ${value.count === 0 ? 'disabled' : ''}
            class="ss-facet__input visually-hidden"
          />
          <span class="ss-facet__visual-label">${value.label}</span>
          <span class="ss-facet__visual-count">(${value.count})</span>
        </label>
      `,
      )
      .join('');
  }

  /**
   * Render visual facet with images. If the facet has a jeans-size split (splitLabel +
   * splitValues set by #buildFinalFacets), renders a second labelled section below.
   * @param {Facet} facet
   * @param {number} activeCount
   * @returns {string}
   */
  #renderVisualFacet(facet, activeCount) {
    const splitActiveCount = facet.splitValues
      ? facet.splitValues.filter((v) => v.active).length
      : 0;
    return `
      <div class="ss-facet ss-facet--visual" data-facet-field="${facet.field}">
        <details class="ss-facet__panel" open>
          <summary class="ss-facet__summary">
            <span class="ss-facet__label">${facet.label}</span>
            ${activeCount > 0 ? `<span>(${activeCount})</span>` : ''}
            <span class="ss-facet__caret">${CARET_SVG}</span>
          </summary>
          <div class="ss-facet__content">
            <div class="ss-facet__visual-grid">
              ${this.#renderVisualItems(facet.field, facet.values)}
            </div>
            ${
              facet.splitLabel
                ? `
              <div class="ss-facet__size-split">
                <div class="ss-facet__label-heading label-jeans-size">
                  ${this.#escapeHtml(facet.splitLabel)}${splitActiveCount > 0 ? ` (${splitActiveCount})` : ''}
                </div>
                <div class="ss-facet__visual-grid">
                  ${this.#renderVisualItems(facet.field, facet.splitValues)}
                </div>
              </div>
            `
                : ''
            }
          </div>
        </details>
      </div>
    `;
  }

  /**
   * Render color swatch facet
   * @param {Facet} facet
   * @param {number} activeCount
   * @returns {string}
   */
  #renderColorSwatchFacet(facet, activeCount) {
    return `
      <div class="ss-facet ss-facet--color" data-facet-field="${facet.field}">
        <details class="ss-facet__panel" open>
          <summary class="ss-facet__summary">
            <span class="ss-facet__label">${facet.label}</span>
            ${
              activeCount > 0
                ? `
              <span class="ss-facet__active-swatches">
                ${facet.values
                  .filter((v) => v.active)
                  .slice(0, 3)
                  .map(
                    (v) => `
                  <span class="ss-facet__mini-swatch" style="${this.#getSwatchStyle(v)}"></span>
                `,
                  )
                  .join('')}
                ${activeCount > 3 ? `<span>(+${activeCount - 3})</span>` : ''}
              </span>
            `
                : ''
            }
            <span class="ss-facet__caret">${CARET_SVG}</span>
          </summary>
          <div class="ss-facet__content">
            <div class="ss-facet__visual-grid">
              ${facet.values
                .map(
                  (value) => `
                <label
                  class="ss-facet__visual-item ${value.active ? 'ss-facet__visual-item--active' : ''} ${value.count === 0 ? 'ss-facet__visual-item--disabled' : ''}"
                  title="${value.label} (${value.count})"
                >
                  <input
                    type="checkbox"
                    name="filter.${facet.field}"
                    value="${value.value}"
                    ${value.active ? 'checked' : ''}
                    ${value.count === 0 ? 'disabled' : ''}
                    class="ss-facet__input"
                  />
                  <span class="ss-facet__swatch" style="${this.#getSwatchStyle(value)}"></span>
                  <span class="ss-facet__visual-label">${value.label}</span>
                  <span class="ss-facet__visual-count">(${value.count})</span>
                </label>
              `,
                )
                .join('')}
            </div>
          </div>
        </details>
      </div>
    `;
  }

  /**
   * Get swatch style (background color or image)
   * @param {FacetValue} value
   * @returns {string}
   */
  #getSwatchStyle(value) {
    // Check for direct color value
    if (value.color) {
      return `background-color: ${value.color};`;
    }

    // Try to get image from prefix
    if (this.colorSwatchPrefix) {
      const colorName = value.value.toLowerCase().replace(/\s+/g, '-');
      return `background-image: url('${this.colorSwatchPrefix}${colorName}.jpg');`;
    }

    // Fallback to color name as CSS color
    const colorName = value.value.toLowerCase().replace(/\s+/g, '');
    return `background-color: ${colorName};`;
  }

  /**
   * Render size facet with alpha/numeric split.
   * For ss_size_clothing with a jeans-size split, renders primary clothing sizes (with `/`)
   * followed by a labelled jeans-size section (without `/`).
   * @param {Facet} facet
   * @param {number} activeCount
   * @returns {string}
   */
  #renderSizeFacet(facet, activeCount) {
    if (facet.splitLabel) {
      const splitActiveCount = facet.splitValues.filter((v) => v.active).length;
      return `
        <div class="ss-facet ss-facet--size" data-facet-field="${facet.field}">
          <details class="ss-facet__panel" open>
            <summary class="ss-facet__summary">
              <span class="ss-facet__label">${facet.label}</span>
              ${activeCount > 0 ? `<span>(${activeCount})</span>` : ''}
              <span class="ss-facet__caret">${CARET_SVG}</span>
            </summary>
            <div class="ss-facet__content">
              <div class="ss-facet__size-pills">
                ${facet.values.map((v) => this.#renderSizePill(facet.field, v)).join('')}
              </div>
              <div class="ss-facet__size-split">
                <span class="ss-facet__size-group-label">
                  ${this.#escapeHtml(facet.splitLabel)}${splitActiveCount > 0 ? ` (${splitActiveCount})` : ''}
                </span>
                <div class="ss-facet__size-pills">
                  ${facet.splitValues.map((v) => this.#renderSizePill(facet.field, v)).join('')}
                </div>
              </div>
            </div>
          </details>
        </div>
      `;
    }

    const alphaSizes = facet.values.filter((v) => ALPHA_SIZE_PATTERN.test(v.value.trim()));
    const numericSizes = facet.values.filter((v) => !ALPHA_SIZE_PATTERN.test(v.value.trim()));

    alphaSizes.sort((a, b) => {
      const aIndex = ALPHA_SIZE_ORDER.findIndex((s) => s.toLowerCase() === a.value.toLowerCase());
      const bIndex = ALPHA_SIZE_ORDER.findIndex((s) => s.toLowerCase() === b.value.toLowerCase());
      return aIndex - bIndex;
    });

    // Sort numeric sizes
    numericSizes.sort((a, b) => parseFloat(a.value) - parseFloat(b.value));

    return `
      <div class="ss-facet ss-facet--size" data-facet-field="${facet.field}">
        <details class="ss-facet__panel" open>
          <summary class="ss-facet__summary">
            <span class="ss-facet__label">${facet.label}</span>
            ${activeCount > 0 ? `<span>(${activeCount})</span>` : ''}
            <span class="ss-facet__caret">${CARET_SVG}</span>
          </summary>
          <div class="ss-facet__content">
            ${
              alphaSizes.length > 0
                ? `
              <div class="ss-facet__size-group">
                <span class="ss-facet__size-group-label">Letter Sizes</span>
                <div class="ss-facet__size-pills">
                  ${alphaSizes.map((value) => this.#renderSizePill(facet.field, value)).join('')}
                </div>
              </div>
            `
                : ''
            }
            ${
              numericSizes.length > 0
                ? `
              <div class="ss-facet__size-group">
                <span class="ss-facet__size-group-label">Numeric Sizes</span>
                <div class="ss-facet__size-pills">
                  ${numericSizes.map((value) => this.#renderSizePill(facet.field, value)).join('')}
                </div>
              </div>
            `
                : ''
            }
          </div>
        </details>
      </div>
    `;
  }

  /**
   * Render size pill
   * @param {string} field
   * @param {FacetValue} value
   * @returns {string}
   */
  #renderSizePill(field, value) {
    return `
      <label class="ss-facet__size-pill ${value.active ? 'ss-facet__size-pill--active' : ''} ${value.count === 0 ? 'ss-facet__size-pill--disabled' : ''}">
        <input
          type="checkbox"
          name="filter.${field}"
          value="${value.value}"
          ${value.active ? 'checked' : ''}
          ${value.count === 0 ? 'disabled' : ''}
          class="ss-facet__input"
        />
        <span class="ss-facet__size-pill-label">${value.label}</span>
      </label>
    `;
  }

  /**
   * Render standard text/list facet (same visual style as visual facets for consistency in drawer)
   * @param {Facet} facet
   * @param {number} activeCount
   * @returns {string}
   */
  #renderTextFacet(facet, activeCount) {
    return `
      <div class="ss-facet ss-facet--text" data-facet-field="${facet.field}">
        <details class="ss-facet__panel" open>
          <summary class="ss-facet__summary">
            <span class="ss-facet__label">${facet.label}</span>
            ${activeCount > 0 ? `<span>(${activeCount})</span>` : ''}
            <span class="ss-facet__caret">${CARET_SVG}</span>
          </summary>
          <div class="ss-facet__content">
            <div class="ss-facet__visual-grid">
              ${facet.values
                .map(
                  (value) => `
                <li class="ss-facet__list-item ${value.active ? 'ss-facet__list-item--active' : ''} ${value.count === 0 ? 'ss-facet__list-item--disabled' : ''}">
                  <label class="ss-facet__checkbox">
                    <input
                      type="checkbox"
                      name="filter.${facet.field}"
                      value="${value.value}"
                      ${value.active ? 'checked' : ''}
                      ${value.count === 0 ? 'disabled' : ''}
                      class="ss-facet__input"
                    />
                    <span class="ss-facet__checkbox-box">
                      <svg class="ss-facet__checkbox-check" viewBox="0 0 12 12" fill="none" aria-hidden="true">${CHECK_SVG}</svg>
                    </span>
                    <span class="ss-facet__checkbox-label">${value.label}</span>
                    <span class="ss-facet__checkbox-count">(${value.count})</span>
                  </label>
                </li>
              `,
                )
                .join('')}
            </div>
          </div>
        </details>
      </div>
    `;
  }

  /**
   * Handle filter checkbox change
   * @param {Event} event
   */
  #handleFilterChange = debounce((event) => {
    const target = event.target;
    if (!target.matches('.ss-facet__input')) return;

    this.#updateFilters();
  }, 0);

  /**
   * Handle click events
   * @param {Event} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Remove single visual facet value (X button)
    const removeVisual = target.closest('[data-ss-remove-visual]');
    if (removeVisual instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      const item = removeVisual.closest('.ss-facet__visual-item');
      const input = item?.querySelector('.ss-facet__input');
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    // Clear all filters
    if (target.closest('[data-ss-clear-all]')) {
      this.#clearAllFilters();
      return;
    }

    // Clear single facet
    const clearFacet = target.closest('[data-ss-clear-facet]');
    if (clearFacet instanceof HTMLElement) {
      const field = clearFacet.dataset.ssClearFacet;
      if (field) this.#clearFacet(field);
    }
  };

  /**
   * Remove all filter.* search params from URL (mutates url)
   * @param {URL} url
   */
  #clearFilterParams(url) {
    [...url.searchParams.keys()].forEach((key) => {
      if (key.startsWith('filter.')) url.searchParams.delete(key);
    });
  }

  /**
   * Update filters and dispatch event
   */
  #updateFilters() {
    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('.ss-facet__input:checked')
    );
    /** @type {string[]} */
    const filters = [];

    inputs.forEach((input) => {
      filters.push(`${input.name}=${encodeURIComponent(input.value)}`);
    });

    const url = new URL(window.location.href);
    this.#clearFilterParams(url);

    // Add new filter params
    inputs.forEach((input) => {
      url.searchParams.append(input.name, input.value);
    });

    // Reset to page 1
    url.searchParams.delete('page');

    // Update URL and dispatch event
    history.pushState({}, '', url.toString());

    performance.mark('facet-filter-dispatched');
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.FilterUpdate, {
        detail: { filters },
      }),
    );
  }

  /**
   * Clear all filters
   */
  #clearAllFilters() {
    const url = new URL(window.location.href);
    this.#clearFilterParams(url);
    url.searchParams.delete('page');

    history.pushState({}, '', url.toString());

    performance.mark('facet-filter-dispatched');
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.FilterUpdate, {
        detail: { filters: [] },
      }),
    );
  }

  /**
   * Clear single facet
   * @param {string} field
   */
  #clearFacet(field) {
    const url = new URL(window.location.href);

    url.searchParams.delete(`filter.${field}`);
    url.searchParams.delete('page');

    history.pushState({}, '', url.toString());

    performance.mark('facet-filter-dispatched');
    document.dispatchEvent(new CustomEvent(ThemeEvents.FilterUpdate));
  }

  /**
   * Update clear all button visibility
   */
  #updateClearAllVisibility() {
    const clearAllEl = this.#getRef('clearAllButton');
    if (!clearAllEl) return;

    const hasActiveFilters = this.querySelectorAll('.ss-facet__input:checked').length > 0;
    clearAllEl.toggleAttribute('hidden', !hasActiveFilters);
  }
}

if (!customElements.get('searchspring-facets')) {
  customElements.define('searchspring-facets', SearchspringFacetsComponent);
}

export { SearchspringFacetsComponent };
